import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { type MouseEvent, type ReactNode, useId, useState } from 'react';

import { cssVar } from '../../theme/tokens';

export interface DropdownMenuItem {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  /** Destructive actions (Delete) in the danger text colour. */
  danger?: boolean;
  /** A divider above this item. */
  dividerBefore?: boolean;
}

/** Props to spread on the trigger button: they open the menu and describe it to screen readers. */
export interface DropdownTriggerProps {
  onClick: (event: MouseEvent<HTMLElement>) => void;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean | undefined;
  'aria-controls': string | undefined;
}

export interface DropdownMenuProps {
  /** Renders the trigger; spread the props on a Button or IconButton. */
  renderTrigger: (props: DropdownTriggerProps) => ReactNode;
  items: readonly DropdownMenuItem[];
  /** Align the menu with the trigger's start or end edge (default end). */
  align?: 'start' | 'end';
  /** Content above the items (for example the signed-in user). */
  header?: ReactNode;
  /** Minimum width of the menu (default 192 px). */
  minWidth?: string;
}

/** A menu of actions opened from a button; arrow keys, Home/End, type-ahead and Escape work. */
export function DropdownMenu({
  renderTrigger,
  items,
  align = 'end',
  header,
  minWidth = `calc(${cssVar('space-12')} * 4)`,
}: DropdownMenuProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const open = Boolean(anchor);
  const horizontal = align === 'end' ? 'right' : 'left';

  return (
    <>
      {renderTrigger({
        onClick: (event) => setAnchor(event.currentTarget),
        'aria-haspopup': 'menu',
        'aria-expanded': open || undefined,
        'aria-controls': open ? menuId : undefined,
      })}
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal }}
        transformOrigin={{ vertical: 'top', horizontal }}
        slotProps={{ paper: { sx: { minWidth } } }}
      >
        {header}
        {items.flatMap((item) => [
          ...(item.dividerBefore ? [<Divider key={`${item.label}-divider`} />] : []),
          <MenuItem
            key={item.label}
            disabled={item.disabled}
            onClick={() => {
              setAnchor(null);
              item.onSelect();
            }}
            sx={item.danger ? { color: cssVar('danger-text') } : undefined}
          >
            {item.icon ? <ListItemIcon sx={{ color: 'inherit' }}>{item.icon}</ListItemIcon> : null}
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>,
        ])}
      </Menu>
    </>
  );
}
